import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session, User } from "@/integrations/supabase/client";

export type AppRole = "admin" | "head_teacher" | "teacher";

export interface Profile {
  id: string;
  user_id: string;
  full_name: string;
  phone: string | null;
  teacher_id: string | null;
  designation: string | null;
  school_id: string | null;
  status: string;
  class_taught: string | null;
}

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  roles: AppRole[];
  loading: boolean;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);

  const loadProfile = async (userId: string) => {
    const [{ data: p, error: profileError }, { data: r, error: rolesError }] = await Promise.all([
      supabase.from("profiles").select("*").eq("user_id", userId).maybeSingle(),
      supabase.from("user_roles").select("role").eq("user_id", userId),
    ]);

    if (profileError) {
      console.error("Failed to load profile", profileError);
      setProfile(null);
    } else {
      setProfile((p as Profile) ?? null);
    }

    if (rolesError) {
      console.error("Failed to load roles", rolesError);
      setRoles([]);
    } else {
      setRoles(((r ?? []) as { role: AppRole }[]).map((x) => x.role));
    }
  };

  useEffect(() => {
    let currentUserId: string | null = null;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event: string, newSession: Session | null) => {
      setSession(newSession);
      const nextUserId = newSession?.user?.id ?? null;

      // Only re-load profile when the user actually changes (sign-in / sign-out / switch user).
      // Ignore TOKEN_REFRESHED / USER_UPDATED so the dashboard doesn't flicker or log out.
      if (nextUserId === currentUserId && event !== "SIGNED_OUT") return;

      currentUserId = nextUserId;
      if (newSession?.user) {
        setLoading(true);
        setProfile(null);
        setRoles([]);
        setTimeout(() => {
          loadProfile(newSession.user.id).finally(() => setLoading(false));
        }, 0);
      } else {
        setProfile(null);
        setRoles([]);
        setLoading(false);
      }
    });

    supabase.auth.getSession().then(({ data }: { data: { session: Session | null } }) => {
      setSession(data.session);
      currentUserId = data.session?.user?.id ?? null;
      if (data.session?.user) {
        setLoading(true);
        loadProfile(data.session.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    setLoading(true);
    await supabase.auth.signOut();
  };

  const refresh = async () => {
    if (!session?.user) return;
    setLoading(true);
    await loadProfile(session.user.id);
    setLoading(false);
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user: session?.user ?? null,
        profile,
        roles,
        loading,
        signOut,
        refresh,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function primaryRole(roles: AppRole[]): AppRole {
  if (roles.includes("admin")) return "admin";
  if (roles.includes("head_teacher")) return "head_teacher";
  return "teacher";
}
